import React from 'react';
import { useTranslation } from 'react-i18next';

import { translatePortfolioCode, translatePortfolioType, formatAmount, fractionToPercent } from 'helpers/helpers';
import Grid from '@material-ui/core/Grid';
import H2 from 'apps/Pdf/components/common/H2';
import SteppedBar from 'components/Bars/SteppedBar';

import useGeneralStyles from 'apps/Pdf/styles/general';
import useStyles from './BucketStyle';
//import { PlanBucketData } from 'apps/Pdf/templates/Interfaces';
import { Bucket } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { AmountValue } from 'common/interfaces';
import { PortfolioType } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { Currency, EdwardColors } from 'enums';

interface Props {
  className?: string;
  term?: 'longTerm' | 'midTerm' | 'shortTerm';
  //data: PlanBucketData;
  data: Bucket;
  originalBucket?: Bucket;
  currency: Currency;
  profit?: AmountValue;
  profitPercent?: number;
  setup?: boolean;
  currentBalance?: AmountValue;
  riskChange?: boolean;
  rebalancingEnabled?: boolean;
  hedgingEnabled?: boolean;
}

const PlanBucket = (props: Props) => {
  const styles = useGeneralStyles();
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    term,
    className,
    data,
    currency,
    setup,
    currentBalance,
    profitPercent,
    riskChange,
    originalBucket,
    rebalancingEnabled,
    //hedgingEnabled,
  } = props;
  const originalBucketChange = originalBucket && data.riskProfile !== originalBucket.riskProfile;
  const bucketChange =
    originalBucketChange || (data.originalRiskProfile !== undefined && data.originalRiskProfile !== data.riskProfile);
  const otherBucketChange = !bucketChange && riskChange;

  return (
    <div className={`${classes.root} bgrLighter${className ? ' ' + className : ''}`}>
      <H2 className={term}>
        {t(data.title)} {!data.active && t('general_inactive')}
      </H2>
      <Grid container>
        <Grid item xs={12} className={styles.supplement}>
          {t(data.subtitle)}
        </Grid>
        {!setup && (
          <Grid item xs={12} className={`${styles.strongText} ${styles.right}`}>
            {formatAmount(currentBalance ? currentBalance : data.balance, currency)}
          </Grid>
        )}
        {props.profit && (
          <>
            <Grid item xs={4}>
              Výnos
            </Grid>
            <Grid item xs={8} className={`${styles.value} ${styles.right}`}>
              {formatAmount(props.profit, currency, true)}
              {profitPercent !== undefined &&
                profitPercent !== null &&
                ` (${fractionToPercent(profitPercent, 2)} p. a.)`}
            </Grid>
          </>
        )}
        <Grid item xs={3}>
          Strategie:
        </Grid>
        <Grid
          item
          xs={9}
          className={`${styles.value} ${styles.right}`}
          style={{ color: originalBucketChange ? EdwardColors.WARNING : undefined }}
        >
          {data.portfolio.code.type === PortfolioType.IND
            ? data.portfolio.code.title
            : translatePortfolioCode(data.portfolio.code.code)}
        </Grid>

        <Grid item xs={3}>
          {data.portfolio.code.type !== PortfolioType.IND && 'Modelová řada:'}
        </Grid>
        <Grid item xs={9} className={`${styles.value} ${styles.right}`}>
          {translatePortfolioType(data.portfolio.code.code, data.portfolio.code.type === PortfolioType.IND)}
        </Grid>

        {(bucketChange || otherBucketChange) && (
          <>
            <Grid item xs={10} style={{ opacity: bucketChange ? 1 : 0, color: '#757575' }}>
              Původní rizikovost strategie:
            </Grid>
            <Grid
              item
              xs={2}
              className={`${styles.bold} ${styles.value} ${styles.right}`}
              style={{ opacity: bucketChange ? 1 : 0, color: '#757575' }}
            >
              {originalBucketChange ? originalBucket!.riskProfile : data.originalRiskProfile}
            </Grid>
            <Grid item xs={12} style={{ opacity: bucketChange ? 1 : 0 }}>
              <SteppedBar
                currentValue={originalBucketChange ? originalBucket!.riskProfile : data.originalRiskProfile!}
                disabled={riskChange}
              />
            </Grid>
          </>
        )}
        <Grid
          item
          xs={10}
          className={bucketChange ? `${styles.bold} colorDark` : undefined}
          style={{ color: originalBucketChange ? EdwardColors.WARNING : undefined }}
        >
          {bucketChange || originalBucketChange ? 'Nová rizikovost stategie' : 'Rizikovost strategie'}:
        </Grid>
        <Grid item xs={2} className={`${styles.bold} ${styles.value} ${styles.right}`}>
          {data.riskProfile}
        </Grid>
        <Grid item xs={12}>
          <SteppedBar currentValue={data.riskProfile!} warning={originalBucketChange} />
        </Grid>

        {!props.profit && (
          <>
            <Grid item xs={8}>
              Kalkulovaný výnos*:
            </Grid>
            <Grid
              item
              xs={4}
              className={`${styles.value} ${styles.right}`}
              style={{
                color:
                  originalBucket && originalBucket.portfolio.income !== data.portfolio.income
                    ? EdwardColors.WARNING
                    : undefined,
              }}
            >
              {fractionToPercent(data.portfolio.income)}
            </Grid>
            <Grid item xs={8}>
              Akceptovatelný pokles:
            </Grid>
            <Grid
              item
              xs={4}
              className={`${styles.value} ${styles.right}`}
              style={{
                color:
                  originalBucket && originalBucket.portfolio.loss !== data.portfolio.loss
                    ? EdwardColors.WARNING
                    : undefined,
              }}
            >
              {fractionToPercent(data.portfolio.loss)}
            </Grid>
          </>
        )}
        {(setup || originalBucket) && (
          <>
            {/*(!originalBucket || hedgingEnabled) && (
              <>
                <Grid item xs={8}>
                  Měnové zajištění
                </Grid>
                <Grid
                  item
                  xs={4}
                  className={`${styles.value} ${styles.right}`}
                  style={{
                    color:
                      originalBucket && originalBucket.currencyHedging !== data.currencyHedging
                        ? EdwardColors.WARNING
                        : undefined,
                  }}
                >
                  {data.currencyHedging ? 'Zapnuto' : 'Vypnuto'}
                </Grid>
              </>
            )*/}
            {(!originalBucket || rebalancingEnabled) && (
              <>
                <Grid item xs={8}>
                  Autom. rebalancování
                </Grid>
                <Grid item xs={4} className={`${styles.value} ${styles.right}`}
                  style={{
                    color:
                      originalBucket && originalBucket.automaticRebalancing !== data.automaticRebalancing
                        ? EdwardColors.WARNING
                        : undefined,
                  }}
                >
                  {data.automaticRebalancing ? 'Zapnuto' : 'Vypnuto'}
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </div>
  );
};

export default PlanBucket;
